import * as axios from 'axios'
import { logoutUser } from '../actions/AuthAction'
import store from '../Store'
import { dump } from './debugService'
import { guessExtensionFromPathAndMime } from './document.utils'
import interceptorResponseRefreshAccessToken from './interceptors/interceptor.response.refreshAccessToken'
import * as routeService from './routeService'
import * as ts from './translationService'
import * as userService from './userService'

const url = process.env.REACT_APP_API
const urlV2 = process.env.REACT_APP_API_V2

const axiosService = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
})

axiosService.interceptors.response.use(
  interceptorResponseRefreshAccessToken.success,
  interceptorResponseRefreshAccessToken.error,
)

axiosService.interceptors.response.use(
  res => res,
  err => {
    dump(err.response)
    const res = err.response
    if (res && res.status === 401) {
      console.warn('Unauthorized, redirecting to login', res)
      store.dispatch(logoutUser({ clearRedirect: false }))
      routeService.goToLoginPage()
    }
    throw err
  },
)

export const fetchApi = (endPoint, payload = {}, method = 'get', headers = {}, { isV2 } = {}) => {
  const storedUserData = userService.getStoredUserData()
  const accessToken = storedUserData.accessToken

  const data =
    method === 'get'
      ? {
          params: payload,
        }
      : {
          data: payload,
        }

  const requestHeaders = {
    ...headers,
    'X-Source': 'intake',
  }
  // Prefer the stored session token, falling back to an Authorization header passed
  // by the caller (used during login, before the token is persisted).
  const token = accessToken || requestHeaders.Authorization
  if (token) {
    // v2 (NestJS bearer strategy) requires the "Bearer " prefix; v1 (loopback) uses
    // the raw token.
    requestHeaders.Authorization = isV2 ? `Bearer ${token}` : token
  }
  data.headers = requestHeaders

  const apiVersion = isV2 ? '/v2' : '/v1'
  const baseUrl = isV2 ? urlV2 : url

  return axiosService({
    method,
    url: /^(f|ht)tps?:\/\//i.test(endPoint) ? endPoint : `${baseUrl}${apiVersion}${endPoint}`,
    ...data,
  })
}

export const fetchApiNext = (endPoint, payload = {}, method = 'get', headers = {}) => {
  const storedUserData = userService.getStoredUserData()
  const accessToken = storedUserData.accessToken

  const requestHeaders = {
    ...headers,
    'X-Source': 'intake',
  }
  if (accessToken) {
    requestHeaders['Authorization'] = `Bearer ${accessToken}`
  }

  const data =
    method === 'get'
      ? {
          params: payload,
        }
      : {
          data: payload,
        }
  data.headers = requestHeaders
  return axiosService({
    method,
    url: /^(f|ht)tps?:\/\//i.test(endPoint) ? endPoint : `${url}${endPoint}`,
    ...data,
  })
}

// Call when create user name, firstname, password
export const createUser = ({ mobileNumber, tacId = 1, email, ...userInfo }) => {
  return fetchApi(
    '/users',
    {
      unverifiedPhone: mobileNumber,
      unverifiedEmail: email,
      captchaVersion: 'legacy',
      ...userInfo,
      locale: ts.mapLocale(ts.currentLocale()),
      tacId: tacId,
    },
    'post',
    {},
    { isV2: true },
  )
}

export const resendVerificationMail = email => {
  return fetchApi(`/users/email/verification/${email}`, {}, 'post')
}

export const resendVerificationSms = mobileNumber => {
  return fetchApi(`/users/sms/verification/${mobileNumber}`, {}, 'post')
}

export const updateUserInfo = user => {
  const { email, mobileNumber, phone, ...userInfo } = user

  // The NestJS UpdateUserDto only accepts unverifiedEmail/unverifiedPhone (changing
  // these requires re-verification), so map the legacy email/phone fields onto them.
  const payload = {
    ...userInfo,
    ...(email !== undefined && { unverifiedEmail: email }),
    ...((mobileNumber || phone) !== undefined && { unverifiedPhone: mobileNumber || phone }),
  }

  return fetchApi('/users/me', payload, 'patch', {}, { isV2: true })
}

export const updateUserLocale = (locale, id) => {
  const storedUserData = userService.getStoredUserData()
  let userID = id || storedUserData.id

  if (userID) fetchApi('/users/me', { locale }, 'patch', {}, { isV2: true })
}

export const verifyPhoneNumber = ({ userID, pincode }) => {
  return fetchApi(`/oauth/confirm-phone?uid=${userID}&token=${pincode}`, {}, 'post')
}

export const getUserInformation = async ({ token } = {}) => {
  const response = await fetchApi(`/users/me`, {}, 'get', { Authorization: token }, { isV2: true })

  // The v2 endpoint returns the user wrapped in a { data } envelope; unwrap it so
  // callers keep reading `response.data` as the user object (the previous v1 contract).
  return { ...response, data: response.data.data }
}

export const requestAccessTokenFromEmail = (userID, token) => {
  return fetchApi(`/oauth/request-access-token-from-email?uid=${userID}&token=${token}`, {}, 'post')
}

export const pingUser = ({ token } = {}) => {
  return fetchApi(`/users/me/ping`, {}, 'get', {
    Authorization: token,
  })
}

export const doesPhoneNumberExist = ({ mobileNumber }) => {
  return fetchApi(`/users/exists/phone/${mobileNumber.replace(/([^0-9|+|/])/g, '').toLowerCase()}`)
}

export const doesEmailExist = email => {
  return fetchApi(`/users/exists/email/${email.replace(/\s+/g, '').toLowerCase()}`)
}

export const doLogin = async ({ password, mobileNumber, email }) => {
  const response = await fetchApi(
    '/auth/sign-in',
    {
      emailOrPhone: email || mobileNumber,
      password,
    },
    'post',
    {},
    { isV2: true },
  )

  return response.data.data
}

export const doTokenLogin = token => fetchApi('/auth/sign-in/token', { token }, 'post', {}, { isV2: true })

export const verifyToken = token => token

export const getBrokerPublicInformation = brokerId => {
  return fetchApi(`/brokers/info/${brokerId}`)
}

export const getTheme = themeName => {
  return fetchApi(`/themes/byName/${themeName}`)
}

export const getPropertyPublicInformation = propertyId => {
  return fetchApi(`/properties/info/${propertyId}?filter={"include":"broker"}`)
}

export const getPhaseInformation = ({ userId, propertyId }) => {
  return fetchApi(`/users/${userId}/application/phase/${propertyId}`)
}

export const updateApplicationProfile = information => {
  const { id } = userService.getStoredUserData()
  return fetchApi(`/users/${id}/application/profile`, information, 'put')
}

export const getApplicationProfile = userId => {
  return fetchApi(`/users/${userId}/application/profile`)
}

export const getApplications = () => {
  return fetchApi(`/applications?filter={"order":["providedAt DESC"]}`)
}

export const removeApplicationAtBroker = applicationId => {
  return fetchApi(`/applications/${applicationId}/requestRemoval`, {}, 'post')
}

export const removeApplicationAtRentio = applicationId => {
  return fetchApi(`/applications/${applicationId}`, {}, 'delete')
}

export const removeApplications = () => {
  return fetchApi('/users/me/application/profile', {}, 'delete')
}

export const removeProfile = () => {
  return fetchApi('/users/me/application/profile', {}, 'delete')
}

const getPreSignedUrl = extension => fetchApi(`/documents/pre-signed-urls`, { extension }, 'post')

export const uploadDocument = async (file, applicationId) => {
  const extension = guessExtensionFromPathAndMime(file.path, file.type)

  // Get presigned URL
  const { data } = await getPreSignedUrl(extension)
  const { preSignedUrl } = data

  // Post file to presigned URL
  const formData = new FormData()
  Object.keys(preSignedUrl.fields).forEach(fieldName => {
    formData.append(fieldName, preSignedUrl.fields[fieldName])
  })
  formData.append('File', file)

  await axios.post(preSignedUrl.url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  // Post presigned URL to Rentio
  return fetchApi(
    `/applicationProfiles/${applicationId}/uploadFiles`,
    {
      preSignedUrl,
      meta: { filename: file.name || 'bestand_huurprofiel' },
    },
    'post',
  )
}

export const removeDocument = (documentId, applicationId) => {
  return fetchApi(`/applicationProfiles/${applicationId}/file/${documentId}`, {}, 'delete')
}

export const sendPdf = (userId, email) => {
  return fetchApi(`/users/${userId}/application/profile/sendToEmail`, { email }, 'post')
}

export const requestCurrentTac = (brokerId = process.env.REACT_APP_RENTIO_BROKER_ID) => {
  return fetchApi(`/brokers/${brokerId}/tac/intake/${ts.currentLocale()}`)
  // return fetchApi(`/system/tac/${ts.currentLocale()}`)
}

const getEnv = () => {
  console.warn({ x: process.env })

  switch (process.env.REACT_APP_ENV) {
    case 'dev':
      return 'dev'

    case 'staging':
      return 'stag'

    case 'demo':
      return 'demo'

    case 'production':
    default:
      return 'prod'
  }
}

export const requestCurrentRequirements = () => {
  return axios.get(`https://frontend-feature-flags.s3-eu-west-1.amazonaws.com/requirements_${getEnv()}.json`)
}

export const getAcceptedTac = id => {
  return fetchApi(`/tac/${id}`)
}

export const updateTac = tacId => {
  return fetchApi(`/users/me/tac/${tacId}/accept`, {}, 'post')
}

export const requestResetPasswordReset = (phoneOrEmail, captcha, isPhone) => {
  const data = isPhone
    ? {
        phone: phoneOrEmail.replace(/([^0-9|+|/])/g, '').toLowerCase(),
      }
    : {
        email: phoneOrEmail.replace(/\s+/g, '').toLowerCase(),
      }
  return fetchApi(
    '/auth/password/request-reset',
    {
      ...data,
      captcha,
      captchaVersion: 'legacy',
    },
    'post',
    {},
    { isV2: true },
  )
}

export const resetPassword = (newPassword, token) => {
  return fetchApi(
    '/auth/password/reset',
    { newPassword },
    'post',
    {
      Authorization: token,
    },
    { isV2: true },
  )
}

export const getPropertyIdForMaxImmoId = (maxImmoBrokerId, reference) => {
  return fetchApi(`/properties/info/MaxImmo/${maxImmoBrokerId}/${reference}`, {}, 'get')
}

export const getPropertyIdForExternalReference = (intakePrefix, reference, integrator) => {
  return fetchApi(
    `/properties/info/extref/${intakePrefix}/${reference}${integrator ? `?integrator=${integrator}` : ''}`,
    {},
    'get',
  )
}

export const getPropertyIdForWhiseId = (intakePrefix, reference) => {
  return fetchApi(`/properties/info/whise/${intakePrefix}/${reference}`, {}, 'get')
}



// WEBPACK FOOTER //
// ./src/services/networkService.js